import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({
      error,
      errorInfo,
    })

    // Log error to external service in production
    if (process.env.NODE_ENV === 'production') {
      // Example: logErrorToService(error, errorInfo)
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-tg-bg p-4 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-lg">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-4">
              An unexpected error occurred. Please try refreshing the page.
            </p>

            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-red-600 font-medium">
                  Error Details {process.env.NODE_ENV === 'development' ? '(Development)' : ''}
                </summary>
                <div className="mt-2">
                  <div className="flex gap-2 mb-2">
                    <button
                      onClick={() => {
                        const errorText = `Error: ${this.state.error?.toString()}\n\nComponent Stack:${this.state.errorInfo?.componentStack || 'Not available'}`
                        navigator.clipboard.writeText(errorText).then(() => {
                          alert('Error details copied to clipboard!')
                        }).catch(() => {
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea')
                          textArea.value = errorText
                          document.body.appendChild(textArea)
                          textArea.select()
                          document.execCommand('copy')
                          document.body.removeChild(textArea)
                          alert('Error details copied to clipboard!')
                        })
                      }}
                      className="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600"
                    >
                      📋 Copy Error
                    </button>
                  </div>
                  <pre className="p-2 bg-red-50 rounded text-xs text-red-800 overflow-auto max-h-40 select-text">
                    <strong>Error:</strong> {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack && (
                      <>
                        <br/><br/>
                        <strong>Component Stack:</strong>
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </pre>
                </div>
              </details>
            )}

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: undefined, errorInfo: undefined })
                }}
                className="flex-1 bg-blue-500 text-white py-2 rounded-lg font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-gray-500 text-white py-2 rounded-lg font-medium"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary